/**
 * Voucher Condition Evaluator
 *
 * Evaluates Magento `magento_conditions` JSON from StorefrontAllocation
 * against the current cart and customer context to determine voucher eligibility.
 *
 * Supports:
 * - Cart subtotal conditions (base_subtotal)
 * - Product SKU conditions (Product\Found + Product)
 * - Order count conditions (order_num, number_placed_order_in_app)
 * - Sales amount conditions (sales_amount)
 * - Recursive Combine nodes with all/any aggregator
 */

import type {
    MagentoCondition,
    VoucherEvaluationContext,
    ConditionEvaluationResult,
    UnmetCondition,
} from './voucher-condition.types';

// Condition type constants matching Magento PHP class names
const CONDITION_TYPE = {
    COMBINE: 'Amasty\\GiftCard\\Model\\CodePool\\Condition\\Combine',
    COMBINE_SALESRULE: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Combine',
    ADDRESS: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Address',
    PRODUCT_FOUND: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product\\Found',
    PRODUCT: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product',
    ORDERS: 'Amasty\\RulesPro\\Model\\Rule\\Condition\\Orders',
    PLACED_ORDER_APP: 'KidsPlaza\\SalesRule\\Model\\Rule\\Condition\\PlacedOrderNumberApp',
    PLACED_ORDER_TOTAL: 'KidsPlaza\\SalesRule\\Model\\Rule\\Condition\\PlacedOrderNumberTotal',
} as const;

const ELIGIBLE_RESULT: ConditionEvaluationResult = { isEligible: true, unmetConditions: [] };

// ── Operator Evaluation ──────────────────────────────────────────────────────

export const evaluateNumericOperator = (actual: number, operator: string, expected: number): boolean => {
    switch (operator) {
        case '>=':
            return actual >= expected;
        case '>':
            return actual > expected;
        case '<=':
            return actual <= expected;
        case '<':
            return actual < expected;
        case '==':
            return actual === expected;
        case '!=':
            return actual !== expected;
        default:
            return false;
    }
};

export const evaluateSkuMatch = (cartSkus: string[], operator: string, conditionValue: string): boolean => {
    const requiredSkus = conditionValue
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    if (operator === '()') {
        return cartSkus.some(sku => requiredSkus.includes(sku));
    }
    if (operator === '!()') {
        return !cartSkus.some(sku => requiredSkus.includes(sku));
    }
    return false;
};

export const evaluateCategoryMatch = (cartCategories: string[], operator: string, conditionValue: string): boolean => {
    const requiredCategories = conditionValue
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    if (operator === '()') {
        return cartCategories.some(cat => requiredCategories.includes(cat));
    }
    if (operator === '!()') {
        return !cartCategories.some(cat => requiredCategories.includes(cat));
    }
    if (operator === '==') {
        return requiredCategories.every(cat => cartCategories.includes(cat));
    }
    return false;
};

// ── Human-Readable Message Generator ─────────────────────────────────────────

const formatCurrency = (amount: number): string => amount.toLocaleString('vi-VN') + 'd';

const generateSubtotalUnmet = (operator: string, required: number, current: number): UnmetCondition => {
    if (operator === '>=' || operator === '>') {
        const deficit = required - current;
        return {
            type: 'SUBTOTAL',
            message: 'Mua them ' + formatCurrency(Math.max(0, deficit)) + ' de du dieu kien',
            currentValue: current,
            requiredValue: required,
            operator,
        };
    }
    return {
        type: 'SUBTOTAL_MAX',
        message: 'Don hang vuot qua ' + formatCurrency(required),
        currentValue: current,
        requiredValue: required,
        operator,
    };
};

const generateOrderCountUnmet = (
    attribute: string,
    operator: string,
    required: number,
    current: number
): UnmetCondition => {
    const deficit = Math.max(0, Math.ceil(required - current));

    if (attribute === 'number_placed_order_in_app') {
        return {
            type: 'APP_ORDER_COUNT',
            message: 'Can dat them ' + deficit + ' don hang tren App',
            currentValue: current,
            requiredValue: required,
            operator,
        };
    }

    if (attribute === 'number_placed_order_total') {
        return {
            type: 'TOTAL_ORDER_COUNT',
            message: 'Can dat them ' + deficit + ' don hang trong he thong',
            currentValue: current,
            requiredValue: required,
            operator,
        };
    }

    if (attribute === 'sales_amount') {
        return {
            type: 'SALES_AMOUNT',
            message: 'Can mua them ' + formatCurrency(Math.max(0, required - current)) + ' tong gia tri don hang',
            currentValue: current,
            requiredValue: required,
            operator,
        };
    }

    return {
        type: 'ORDER_COUNT',
        message: 'Can dat them ' + deficit + ' don hang hoan thanh',
        currentValue: current,
        requiredValue: required,
        operator,
    };
};

// ── Single Condition Evaluator ───────────────────────────────────────────────

const evaluateSingleCondition = (
    condition: MagentoCondition,
    context: VoucherEvaluationContext
): ConditionEvaluationResult => {
    const { type, attribute, operator, value, conditions: children } = condition;

    // Address Condition (base_subtotal)
    if (type === CONDITION_TYPE.ADDRESS && attribute === 'base_subtotal' && operator && value) {
        const expected = parseFloat(value);
        const isEligible = evaluateNumericOperator(context.cartSubtotal, operator, expected);
        return {
            isEligible,
            unmetConditions: isEligible ? [] : [generateSubtotalUnmet(operator, expected, context.cartSubtotal)],
        };
    }

    // Address Condition (total_qty_expr) — Total cart quantity
    if (type === CONDITION_TYPE.ADDRESS && attribute === 'total_qty_expr' && operator && value) {
        const expected = parseFloat(value);
        const actual = context.cartTotalQuantity ?? 0;
        const isEligible = evaluateNumericOperator(actual, operator, expected);
        return {
            isEligible,
            unmetConditions: isEligible
                ? []
                : [
                      {
                          type: 'CART_QUANTITY',
                          message: 'Can them ' + Math.max(0, Math.ceil(expected - actual)) + ' san pham trong gio hang',
                          currentValue: actual,
                          requiredValue: expected,
                          operator,
                      },
                  ],
        };
    }

    // Product Found Condition (SKU check)
    if (type === CONDITION_TYPE.PRODUCT_FOUND && children?.length) {
        const productConditions = children.filter(c => c.type === CONDITION_TYPE.PRODUCT);
        for (const pc of productConditions) {
            // SKU condition
            if (pc.attribute === 'sku' && pc.operator && pc.value) {
                const matched = evaluateSkuMatch(context.cartItemSkus, pc.operator, pc.value);
                const shouldExist = condition.value === '1';
                const isEligible = shouldExist ? matched : !matched;
                if (!isEligible) {
                    const skus = pc.value
                        .split(',')
                        .map(s => s.trim())
                        .join(', ');
                    return {
                        isEligible: false,
                        unmetConditions: [
                            {
                                type: 'PRODUCT_REQUIRED',
                                message: 'Can co san pham ' + skus + ' trong gio hang',
                            },
                        ],
                    };
                }
            }

            // Category condition within Product Found
            if (pc.attribute === 'category_ids' && pc.operator && pc.value && context.cartItemCategories) {
                const matched = evaluateCategoryMatch(context.cartItemCategories, pc.operator, pc.value);
                const shouldExist = condition.value === '1';
                const isEligible = shouldExist ? matched : !matched;
                if (!isEligible) {
                    return {
                        isEligible: false,
                        unmetConditions: [
                            {
                                type: 'CATEGORY_REQUIRED',
                                message: 'Can co san pham thuoc danh muc yeu cau trong gio hang',
                            },
                        ],
                    };
                }
            }
        }
        return ELIGIBLE_RESULT;
    }

    // Orders Condition (order_num, sales_amount)
    if (type === CONDITION_TYPE.ORDERS && attribute && operator && value) {
        const expected = parseFloat(value);
        const actual = attribute === 'order_num' ? context.customerCompletedOrders : context.customerSalesAmount;
        const isEligible = evaluateNumericOperator(actual, operator, expected);
        return {
            isEligible,
            unmetConditions: isEligible ? [] : [generateOrderCountUnmet(attribute, operator, expected, actual)],
        };
    }

    // PlacedOrderNumberApp Condition
    if (type === CONDITION_TYPE.PLACED_ORDER_APP && attribute === 'number_placed_order_in_app' && operator && value) {
        const expected = parseFloat(value);
        const isEligible = evaluateNumericOperator(context.customerAppOrders, operator, expected);
        return {
            isEligible,
            unmetConditions: isEligible
                ? []
                : [generateOrderCountUnmet(attribute, operator, expected, context.customerAppOrders)],
        };
    }

    // PlacedOrderNumberTotal Condition (all orders in system)
    if (type === CONDITION_TYPE.PLACED_ORDER_TOTAL && attribute === 'number_placed_order_total' && operator && value) {
        const expected = parseFloat(value);
        const actual = context.customerTotalOrders ?? 0;
        const isEligible = evaluateNumericOperator(actual, operator, expected);
        return {
            isEligible,
            unmetConditions: isEligible ? [] : [generateOrderCountUnmet(attribute, operator, expected, actual)],
        };
    }

    // Unknown condition type - don't block, let backend validate on apply
    return ELIGIBLE_RESULT;
};

// ── Main Evaluator (Recursive) ──────────────────────────────────────────────

export const evaluateVoucherConditions = (
    magentoConditions: MagentoCondition | null | undefined,
    context: VoucherEvaluationContext
): ConditionEvaluationResult => {
    if (!magentoConditions) {
        return ELIGIBLE_RESULT;
    }

    const { conditions: children, aggregator = 'all' } = magentoConditions;

    if (!children || children.length === 0) {
        return ELIGIBLE_RESULT;
    }

    const results = children.map(child => {
        if (child.type === CONDITION_TYPE.COMBINE || child.type === CONDITION_TYPE.COMBINE_SALESRULE) {
            return evaluateVoucherConditions(child, context);
        }
        return evaluateSingleCondition(child, context);
    });

    const allUnmet = results.flatMap(r => r.unmetConditions);

    if (aggregator === 'all') {
        const isEligible = results.every(r => r.isEligible);
        return { isEligible, unmetConditions: isEligible ? [] : allUnmet };
    }

    const isEligible = results.some(r => r.isEligible);
    return { isEligible, unmetConditions: isEligible ? [] : allUnmet };
};
